/*
 * Security. Three jobs on one page: how you sign in (password, or a provider), a second factor
 * (TOTP with backup codes), and where you are signed in right now with the means to end any session
 * but this one.
 */
import {z} from 'zod';
import {toast} from 'react-toastify';
import {observer} from 'mobx-react-lite';
import React, {FC, useState} from 'react';
import {zodResolver} from '@hookform/resolvers/zod';
import {format, formatDistanceToNow} from 'date-fns';
import {useForm, FormProvider} from 'react-hook-form';
import {
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import {changePassword, IUserLoginSession, revokeOtherSessions, revokeSession, VerdocsEndpoint} from '@verdocs/js-sdk';
import {DisableMFADialog, EnrollMFADialog, OkCancelDialog, RegenerateBackupCodesDialog} from '../../Dialogs';
import {invalidateQuery, useMyUser, useSessions} from '../../react-sdk/queries';
import {Button, FlexFill, HelpDialog, Input, Label, PageTitle, Spinner} from '../../Components';
import {track} from '../../Managers/Analytics';

const TOTAL_BACKUP_CODES = 10;
const STALE_SESSION_MS = 30 * 86_400_000;

const PROVIDER_LABELS: Record<string, string> = {google: 'Google', microsoft: 'Microsoft', apple: 'Apple', github: 'GitHub'};

const Card: FC<{children: React.ReactNode; className?: string}> = ({children, className = ''}) => (
  <div className={`rounded-lg border border-[#ebecf0] bg-white p-5 ${className}`}>{children}</div>
);

const SectionTitle: FC<{children: React.ReactNode}> = ({children}) => (
  <div className="mb-3 text-[15px] font-medium text-[#33364b]">{children}</div>
);

const isPasswordComplex = (password: string) => {
  const counts = {upper: 0, lower: 0, special: 0};
  for (const ch of password) {
    if (/[A-Z]/.test(ch)) counts.upper++;
    else if (/[a-z]/.test(ch)) counts.lower++;
    else if (/[`!@#$%^&*()_\-+=\[\]{};':"\\|,.<>\/?~ ]/.test(ch)) counts.special++;
  }
  return password.length >= 8 && counts.lower > 0 && counts.upper > 0 && counts.special > 0;
};

const schema = z
  .object({
    password: z.string().min(1, 'Enter your current password').max(30),
    new_password: z.string().min(1, 'Enter your new password').max(30),
    confirm_password: z.string().min(1, 'Confirm your new password'),
  })
  .refine((data) => data.new_password === data.confirm_password, {path: ['confirm_password'], message: 'Passwords do not match.'})
  .refine((data) => isPasswordComplex(data.new_password), {
    path: ['confirm_password'],
    message: 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one special character.',
  });

type SchemaType = z.infer<typeof schema>;

const SessionRow: FC<{session: IUserLoginSession; onEnd: () => void}> = ({session, onEnd}) => {
  const Icon = session.mobile ? DevicePhoneMobileIcon : ComputerDesktopIcon;
  const client = [session.browser, session.platform].filter(Boolean).join(' on ') || 'Unknown client';

  return (
    <div className="flex items-center gap-3 border-b border-[#f2f3f5] py-3 last:border-0">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#f2f3f5]">
        <Icon className="h-5 w-5 text-gray-500" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-[13px] font-medium text-[#33364b]">
          {client}
          {session.location ? ` · ${session.location}` : ''}
          {session.current && (
            <span className="rounded-full bg-[#e6f4ec] px-2 py-0.5 text-[11px] font-medium text-[#1f7a4c]">This device</span>
          )}
        </div>
        <div className="text-[12px] text-gray-500">
          {session.current ? 'Active now' : `Last used ${formatDistanceToNow(new Date(session.last_seen_at), {addSuffix: true})}`}
          {session.ip_address ? <> · <span className="font-mono">{session.ip_address}</span></> : null}
        </div>
      </div>
      {!session.current && <Button size="xsmall" variant="secondary" label="Log out" onClick={onEnd} />}
    </div>
  );
};

export const Security: FC = observer(() => {
  const {data: user, isLoading: userLoading} = useMyUser();
  const {data: sessions = [], isLoading: sessionsLoading} = useSessions();

  const [submitting, setSubmitting] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [ending, setEnding] = useState<IUserLoginSession | null>(null);

  const form = useForm<SchemaType>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: {password: '', new_password: '', confirm_password: ''},
  });

  const mfa = user?.mfa;
  const providers = user?.sign_in_providers || [];
  const hasPassword = user?.has_password !== false;
  const others = sessions.filter((session) => !session.current);
  const stale = others.filter((session) => Date.now() - new Date(session.last_seen_at).getTime() > STALE_SESSION_MS);
  const backupCodesLeft = mfa?.backup_codes_remaining ?? 0;
  const lowOnCodes = !!mfa?.enabled && backupCodesLeft <= 3;

  const refreshSecurity = () => {
    invalidateQuery(['user', 'me']);
    invalidateQuery(['user', 'mfa']);
    invalidateQuery(['user', 'sessions']);
  };

  const onSubmit = (values: SchemaType) => {
    setSubmitting(true);
    changePassword(VerdocsEndpoint.getDefault(), {old_password: values.password, new_password: values.new_password})
      .then(() => {
        setSubmitting(false);
        form.reset({password: '', new_password: '', confirm_password: ''});
        refreshSecurity();
        track('settings_updated', {section: 'security'});
        toast.success('Your password has been reset.');
      })
      .catch((e) => {
        setSubmitting(false);
        toast.error(e.response?.data?.error || 'Password reset failed. Please check your inputs and try again.');
      });
  };

  const handleEndSession = () => {
    if (!ending) return;
    const target = ending;
    setEnding(null);
    revokeSession(VerdocsEndpoint.getDefault(), target.id)
      .then(() => {
        invalidateQuery(['user', 'sessions']);
        toast.success('That device has been logged out.');
      })
      .catch((e) => toast.error(e.response?.data?.error || 'Unable to log out that device.'));
  };

  const handleEndOthers = () => {
    setConfirmAll(false);
    revokeOtherSessions(VerdocsEndpoint.getDefault())
      .then((r) => {
        invalidateQuery(['user', 'sessions']);
        toast.success(r.revoked === 1 ? 'One other device has been logged out.' : `${r.revoked} other devices have been logged out.`);
      })
      .catch((e) => toast.error(e.response?.data?.error || 'Unable to log out the other devices.'));
  };

  return (
    <div className="w-full max-w-[860px] pb-10">
      <PageTitle>
        Security
        <FlexFill />
        <HelpDialog title="Security">
          <p>
            How you sign in, a second factor to protect it, and every device currently signed in as you. If anything on the device list is
            not yours, log it out here and change your password.
          </p>
        </HelpDialog>
      </PageTitle>

      {userLoading ? (
        <Spinner />
      ) : (
        <>
          <Card className="mb-4">
            <SectionTitle>{hasPassword ? 'Password' : 'How you sign in'}</SectionTitle>
            {hasPassword ? (
              <>
                <div className="mb-4 text-[13px] text-gray-500">
                  {user?.password_changed_at
                    ? `Last changed ${formatDistanceToNow(new Date(user.password_changed_at), {addSuffix: true})}.`
                    : 'Never changed.'}
                  {providers.length > 0 && (
                    <> You can also sign in with {providers.map((provider) => PROVIDER_LABELS[provider] || provider).join(' or ')}.</>
                  )}
                </div>

                <FormProvider {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className="grid grid-cols-1 gap-x-4 tablet:grid-cols-3">
                      <div className="mb-4 flex flex-col gap-2">
                        <Label>Current password</Label>
                        <Input type="password" {...form.register('password')} />
                      </div>
                      <div className="mb-4 flex flex-col gap-2">
                        <Label>New password</Label>
                        <Input type="password" {...form.register('new_password')} />
                      </div>
                      <div className="mb-4 flex flex-col gap-2">
                        <Label>Confirm new password</Label>
                        <Input type="password" {...form.register('confirm_password')} />
                      </div>
                    </div>
                    {form.formState.errors.confirm_password && (
                      <div className="mb-3 text-[12px] text-[#bb2e3e]">{form.formState.errors.confirm_password.message}</div>
                    )}
                    <Button type="submit" size="small" label="Change password" disabled={submitting || !form.formState.isValid} />
                  </form>
                </FormProvider>
              </>
            ) : (
              <div className="text-[13px] text-gray-500">
                You sign in with your {providers.map((provider) => PROVIDER_LABELS[provider] || provider).join(' or ') || 'provider'} account,
                so there is no Verdocs password to change. Password and recovery settings live with your provider.
              </div>
            )}
          </Card>

          <Card className="mb-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <SectionTitle>Two-factor authentication</SectionTitle>
                  <span className="mb-3">
                    {mfa?.enabled ? (
                      <span className="rounded-full bg-[#e6f4ec] px-2 py-0.5 text-[11px] font-medium text-[#1f7a4c]">On</span>
                    ) : (
                      <span className="rounded-full bg-[#f2f3f5] px-2 py-0.5 text-[11px] font-medium text-gray-500">Off</span>
                    )}
                  </span>
                </div>
                <div className="text-[13px] text-gray-500">
                  {mfa?.enabled ? (
                    <>
                      Authenticator app{mfa.enrolled_at ? `, set up ${format(new Date(mfa.enrolled_at), 'MMMM d, yyyy')}` : ''}.{' '}
                      {backupCodesLeft} of {TOTAL_BACKUP_CODES} backup codes left.
                    </>
                  ) : (
                    <>
                      A six-digit code from an app on your phone, asked for at sign-in alongside your password. It stops a leaked password
                      from being enough on its own.
                    </>
                  )}
                </div>
              </div>
              {mfa?.enabled ? (
                <div className="flex flex-wrap gap-2">
                  <Button size="small" variant="secondary" label="New backup codes" onClick={() => setRegenerating(true)} />
                  <Button size="small" variant="secondary" label="Turn off" onClick={() => setDisabling(true)} />
                </div>
              ) : (
                <Button size="small" label="Set up" icon={<ShieldCheckIcon className="h-4 w-4" />} onClick={() => setEnrolling(true)} />
              )}
            </div>

            {lowOnCodes && (
              <div className="mt-4 flex items-start gap-2 rounded-md bg-[#fdf1e3] p-3">
                <ShieldExclamationIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#95591a]" />
                <div className="text-[12px] text-[#95591a]">
                  Only {backupCodesLeft} backup {backupCodesLeft === 1 ? 'code' : 'codes'} left. Generate a new set before they run out; the
                  old ones stop working when you do.
                </div>
              </div>
            )}
          </Card>

          <Card>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <SectionTitle>Where you are logged in</SectionTitle>
                <div className="mb-2 text-[13px] text-gray-500">
                  {sessions.length} {sessions.length === 1 ? 'device' : 'devices'}, including this one.
                  {stale.length > 0 && (
                    <>
                      {' '}
                      {stale.length} {stale.length === 1 ? 'has' : 'have'} not been used in over a month.
                    </>
                  )}
                </div>
              </div>
              {others.length > 0 && (
                <Button size="small" variant="secondary" label="Log out of all other devices" onClick={() => setConfirmAll(true)} />
              )}
            </div>

            {sessionsLoading ? (
              <Spinner />
            ) : (
              sessions.map((session) => <SessionRow key={session.id} session={session} onEnd={() => setEnding(session)} />)
            )}

            <div className="mt-3 text-[12px] text-gray-500">
              Locations come from the network address and can be off by a city. A device you do not recognize is a reason to log it out and
              change your password, in that order.
            </div>
          </Card>
        </>
      )}

      {enrolling && (
        <EnrollMFADialog
          onClose={() => setEnrolling(false)}
          onEnrolled={() => {
            setEnrolling(false);
            refreshSecurity();
            toast.success('Two-factor authentication is on.');
          }}
        />
      )}

      {disabling && (
        <DisableMFADialog
          onClose={() => setDisabling(false)}
          onDisabled={() => {
            setDisabling(false);
            refreshSecurity();
          }}
        />
      )}

      {regenerating && (
        <RegenerateBackupCodesDialog
          onClose={() => setRegenerating(false)}
          onDone={() => {
            setRegenerating(false);
            refreshSecurity();
          }}
        />
      )}

      {ending && (
        <OkCancelDialog onOk={handleEndSession} onCancel={() => setEnding(null)}>
          <div className="text-[13px] text-gray-500">
            {[ending.browser, ending.platform].filter(Boolean).join(' on ') || 'That device'}
            {ending.location ? ` in ${ending.location}` : ''}, last used {formatDistanceToNow(new Date(ending.last_seen_at), {addSuffix: true})}.
            It will need to sign in again. Nothing else changes.
          </div>
        </OkCancelDialog>
      )}

      {confirmAll && (
        <OkCancelDialog onOk={handleEndOthers} onCancel={() => setConfirmAll(false)}>
          <div className="text-[13px] text-gray-500">
            {others.length} {others.length === 1 ? 'device signs' : 'devices sign'} out. This one stays signed in. If you are doing this
            because something looked wrong, change your password next.
          </div>
        </OkCancelDialog>
      )}
    </div>
  );
});
